import { initializeGlassIntro } from './intro.js';

/** Hero owns its state and behavior; dependencies are passed in by main. */

export function register(app) {
  const { hero, layout, navigation, ui } = app;

  hero.getHeroFixedDuration = function getHeroFixedDuration(video) {
    const value = Number.parseInt(video?.dataset?.heroDuration || "", 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  };

  hero.getHeroVideoStart = function getHeroVideoStart(video) {
    const value = Number.parseFloat(video?.dataset?.heroStart || "0");
    return Number.isFinite(value) && value >= 0 ? value : 0;
  };

  hero.getHeroVideoLead = function getHeroVideoLead(video) {
    const value = Number.parseInt(video?.dataset?.heroLead || "", 10);
    return Number.isFinite(value) && value >= 0 ? value : 0;
  };

  hero.getHeroMinDelay = function getHeroMinDelay(video) {
    const value = Number.parseInt(video?.dataset?.heroMinDelay || "", 10);
    return Number.isFinite(value) && value >= 0 ? value : 1400;
  };

  hero.getHeroInternalIndex = function getHeroInternalIndex() {
    return hero.heroIndex;
  };

  hero.clampHeroIndex = function clampHeroIndex(index) {
    if (!hero.heroSlideCount) return 0;
    return (
      ((index % hero.heroSlideCount) + hero.heroSlideCount) %
      hero.heroSlideCount
    );
  };

  hero.isHeroHomeActive = function isHeroHomeActive() {
    const homeView = document.getElementById("view-home");
    return !!(homeView && homeView.classList.contains("active-view"));
  };

  hero.isHeroInView = function isHeroInView() {
    if (!hero.isHeroHomeActive()) return false;
    const rect = hero.heroSlider?.getBoundingClientRect();
    return !rect || (rect.bottom > 0 && rect.top < window.innerHeight);
  };

  let lastVisibility;
  hero.syncHeroViewport = function syncHeroViewport() {
    const visible = hero.isHeroInView();
    if (lastVisibility === visible) return;
    lastVisibility = visible;
    if (!visible) {
      hero.stopHeroAuto();
      hero.syncHeroMediaPlayback();
    } else if (hero.proposalHeroReleased) {
      hero.preloadHeroNeighbors(hero.heroIndex);
      hero.resumeHeroMediaPlayback();
    }
  };

  hero.pauseHeroVideoAtIndex = function pauseHeroVideoAtIndex(index) {
    if (!hero.heroTrack || index < 0) return;
    const slide = hero.heroTrack.children[index];
    const video = slide ? slide.querySelector("video") : null;
    if (video) video.pause();
  };

  hero.updateHeroDots = function updateHeroDots() {
    hero.heroDots.forEach((dot, i) => {
      dot.classList.toggle("active", i === hero.heroIndex);
    });
  };

  hero.getHeroCopyLang = function getHeroCopyLang() {
    return document.documentElement.getAttribute("lang") === "en" ? "en" : "sk";
  };

  hero.shouldShowHeroCopy = function shouldShowHeroCopy(index) {
    return hero.clampHeroIndex(index) !== 0;
  };

  hero.getHeroCopy = function getHeroCopy(index) {
    const copyIndex =
      Math.max(0, hero.clampHeroIndex(index) - 1) % hero.heroCopyItems.length;
    const item = hero.heroCopyItems[copyIndex];
    if (!item) return ["", ""];
    return item[hero.getHeroCopyLang()] || item.sk || item.en || ["", ""];
  };

  hero.getDirectHeroSlideCopy = function getDirectHeroSlideCopy(slide) {
    if (!slide) return null;
    return (
      Array.from(slide.children).find(
        (child) => child.classList && child.classList.contains("hero-copy"),
      ) || null
    );
  };

  hero.renderHeroSlideCopies = function renderHeroSlideCopies() {
    if (!hero.heroTrack || !hero.heroCopyItems.length) return;
    Array.from(hero.heroTrack.children).forEach((slide, index) => {
      let copyEl = hero.getDirectHeroSlideCopy(slide);
      if (!hero.shouldShowHeroCopy(index)) {
        if (copyEl) copyEl.remove();
        return;
      }
      if (!copyEl) {
        copyEl = document.createElement("div");
        copyEl.className = "hero-copy";
        copyEl.setAttribute("aria-hidden", "true");
        copyEl.innerHTML = "<h1></h1><p></p>";
        slide.appendChild(copyEl);
      }
      const copy = hero.getHeroCopy(index);
      const title = copyEl.querySelector("h1");
      const text = copyEl.querySelector("p");
      if (title) title.textContent = copy[0];
      if (text) text.textContent = copy[1];
    });
  };

  hero.updateHeroCopy = function updateHeroCopy(index, instant = false) {
    if (
      !hero.heroCopyEl ||
      !hero.heroCopyTitle ||
      !hero.heroCopyText ||
      !hero.heroCopyItems.length
    )
      return;
    const showCopy = hero.shouldShowHeroCopy(index);
    const copy = showCopy ? hero.getHeroCopy(index) : ["", ""];
    const applyCopy = () => {
      hero.heroCopyTitle.textContent = copy[0];
      hero.heroCopyText.textContent = copy[1];
    };
    if (hero.heroCopyTimer) {
      clearTimeout(hero.heroCopyTimer);
      hero.heroCopyTimer = null;
    }
    if (instant) {
      if (showCopy) applyCopy();
      hero.heroCopyEl.classList.toggle("is-hidden", !showCopy);
      hero.heroCopyEl.classList.remove("is-changing");
      return;
    }
    hero.heroCopyEl.classList.add("is-changing");
    if (showCopy && hero.heroCopyEl.classList.contains("is-hidden"))
      applyCopy();
    hero.heroCopyTimer = setTimeout(() => {
      if (showCopy) applyCopy();
      hero.heroCopyEl.classList.toggle("is-hidden", !showCopy);
      hero.heroCopyEl.classList.remove("is-changing");
      hero.heroCopyTimer = null;
    }, hero.HERO_COPY_SWAP_DELAY_MS);
  };

  hero.warmHeroMedia = function warmHeroMedia(targetIndex) {
    if (!hero.heroTrack || !hero.heroSlideCount || !hero.isHeroInView()) return;
    const resolvedIndex = hero.clampHeroIndex(targetIndex);
    const slide = hero.heroTrack.children[resolvedIndex];
    if (!slide) return;
    const video = slide.querySelector("video");
    if (video) {
      const sourceElement = video.querySelector("source");
      const deferred = sourceElement?.dataset.src && !sourceElement.hasAttribute("src");
      if (deferred) {
        video.poster = video.dataset.poster;
        sourceElement.src = sourceElement.dataset.src;
      }
      const source = video.currentSrc || sourceElement?.src || video.src;
      if (source && hero.heroPreloadedVideos.has(source)) return;
      video.preload = "auto";
      if (deferred || video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
      if (source) hero.heroPreloadedVideos.add(source);
      return;
    }
    const image = slide.querySelector("img");
    if (!image) return;
    if (hero.heroPreloadedImages.has(image.currentSrc || image.src)) return;
    const preloadImage = new Image();
    preloadImage.decoding = "async";
    preloadImage.loading = "eager";
    preloadImage.src = image.currentSrc || image.src;
    hero.heroPreloadedImages.add(image.currentSrc || image.src);
  };

  hero.preloadHeroNeighbors = function preloadHeroNeighbors(activeIndex) {
    if (!hero.isHeroInView()) return;
    hero.warmHeroMedia(activeIndex);
    const activeSlide =
      hero.heroTrack?.children[hero.clampHeroIndex(activeIndex)] || null;
    const activeVideo = activeSlide ? activeSlide.querySelector("video") : null;
    const warmNext = () => hero.warmHeroMedia(activeIndex + 1);
    if (!activeVideo || activeVideo.readyState >= 2) {
      warmNext();
      return;
    }
    if (hero.heroNeighborWarmQueued.has(activeVideo)) return;
    hero.heroNeighborWarmQueued.add(activeVideo);
    activeVideo.addEventListener("loadeddata", () => {
      hero.heroNeighborWarmQueued.delete(activeVideo);
      if (hero.heroIndex === hero.clampHeroIndex(activeIndex)) warmNext();
    }, { once: true });
  };

  hero.syncHeroMediaPlayback = function syncHeroMediaPlayback() {
    if (!hero.heroTrack) return;
    if (!hero.proposalHeroReleased || !hero.isHeroInView()) {
      hero.heroTrack
        .querySelectorAll("video")
        .forEach((video) => video.pause());
      return;
    }
    const activeInternalIndex = hero.getHeroInternalIndex();
    const slides = Array.from(hero.heroTrack.children);
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeInternalIndex;
      const keepOutgoingPlaying =
        hero.heroIsAnimating && slideIndex === hero.heroOutgoingIndex;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      const video = slide.querySelector("video");
      if (!video) return;
      const shouldPlay = isActive || keepOutgoingPlaying;
      if (shouldPlay) {
        if (
          isActive &&
          video.readyState === 0 &&
          !hero.heroActivatedVideos.has(video)
        ) {
          hero.heroActivatedVideos.add(video);
          if (video.networkState === HTMLMediaElement.NETWORK_EMPTY)
            video.load();
        }
        if (isActive && activeInternalIndex !== hero.heroLastInternalIndex) {
          const startAt = hero.getHeroVideoStart(video);
          if (startAt >= 0 && Math.abs(video.currentTime - startAt) > 0.05) {
            const seekToStart = () => {
              if (hero.getHeroInternalIndex() !== activeInternalIndex) return;
              if (!hero.isHeroInView()) {
                // Apply the initial seek on the next visible playback instead.
                hero.heroLastInternalIndex = -1;
                return;
              }
              try {
                video.currentTime = startAt;
              } catch (error) {}
            };
            if (video.readyState >= 1) seekToStart();
            else
              video.addEventListener("loadedmetadata", seekToStart, {
                once: true,
              });
          }
        }
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else {
        video.pause();
      }
    });
    hero.heroLastInternalIndex = activeInternalIndex;
  };

  hero.resumeHeroMediaPlayback = function resumeHeroMediaPlayback() {
    if (!hero.heroTrack || !hero.proposalHeroReleased || !hero.isHeroInView()) return;
    hero.syncHeroMediaPlayback();
    // Intro completion and layout recovery can request this more than once.
    // Keep the current clip's deadline instead of starting its interval again.
    if (!hero.heroIsAnimating && hero.heroAutoTimer == null)
      hero.startHeroAuto();
  };

  hero.queueHeroMediaResume = function queueHeroMediaResume() {
    requestAnimationFrame(hero.resumeHeroMediaPlayback);
    setTimeout(hero.resumeHeroMediaPlayback, 90);
  };

  hero.applyHeroTransform = function applyHeroTransform(animate = true) {
    if (!hero.heroTrack) return;
    if (!animate) {
      hero.heroTrack.classList.add("hero-track-no-transition");
    }
    hero.syncHeroMediaPlayback();
    hero.updateHeroDots();
    if (!animate) {
      void hero.heroTrack.offsetWidth;
      hero.heroTrack.classList.remove("hero-track-no-transition");
    }
  };

  hero.setHeroSlide = function setHeroSlide(index, options = {}) {
    if (!hero.heroSlideCount || !hero.heroTrack) return;
    const { animate = true } = options;
    const nextIndex = hero.clampHeroIndex(index);
    const previousIndex = hero.heroIndex;
    if (hero.heroTransitionTimer) {
      clearTimeout(hero.heroTransitionTimer);
      hero.heroTransitionTimer = null;
    }
    if (
      hero.heroOutgoingIndex >= 0 &&
      hero.heroOutgoingIndex !== previousIndex
    ) {
      hero.pauseHeroVideoAtIndex(hero.heroOutgoingIndex);
    }
    hero.stopHeroAuto();
    hero.heroIndex = nextIndex;
    hero.preloadHeroNeighbors(nextIndex);
    if (!animate || nextIndex === previousIndex) {
      hero.heroIsAnimating = false;
      hero.heroOutgoingIndex = -1;
      hero.applyHeroTransform(false);
      hero.startHeroAuto();
      return;
    }
    hero.heroIsAnimating = true;
    hero.heroOutgoingIndex = previousIndex;
    hero.applyHeroTransform(true);
    hero.heroTransitionTimer = setTimeout(() => {
      hero.heroIsAnimating = false;
      hero.pauseHeroVideoAtIndex(hero.heroOutgoingIndex);
      hero.heroOutgoingIndex = -1;
      hero.heroTransitionTimer = null;
      hero.startHeroAuto();
    }, hero.HERO_TRANSITION_MS + 40);
  };

  hero.getHeroDefaultIndex = function getHeroDefaultIndex() {
    return 0;
  };

  hero.startHeroAuto = function startHeroAuto() {
    if (!hero.proposalHeroReleased) return;
    if (!hero.heroTrack || hero.heroSlideCount < 2 || !hero.isHeroInView())
      return;
    hero.stopHeroAuto();
    const activeInternalIndex = hero.getHeroInternalIndex();
    const activeSlide = hero.heroTrack.children[activeInternalIndex] || null;
    const activeVideo = activeSlide ? activeSlide.querySelector("video") : null;
    if (!activeVideo) {
      hero.heroAutoTimer = setTimeout(() => {
        if (hero.heroIsAnimating) return;
        hero.advanceHeroSequence();
      }, hero.HERO_FALLBACK_MS);
      return;
    }
    const fixedDuration = hero.getHeroFixedDuration(activeVideo);
    const minDelay = hero.getHeroMinDelay(activeVideo);
    const startAt = hero.getHeroVideoStart(activeVideo);
    const elapsed = Math.round(Math.max(0,
      (Number(activeVideo.currentTime) || 0) - startAt,
    ) * 1000);
    if (fixedDuration > 0) {
      if (activeVideo.readyState < 2 || activeVideo.seeking) {
        const beginWhenReady = () => {
          const currentSlide =
            hero.heroTrack.children[hero.getHeroInternalIndex()] || null;
          if (
            currentSlide?.contains(activeVideo) &&
            !hero.heroIsAnimating &&
            activeVideo.readyState >= 2 &&
            !activeVideo.seeking
          )
            hero.startHeroAuto();
        };
        const events = ["loadeddata", "canplay", "seeked", "playing"];
        events.forEach((event) =>
          activeVideo.addEventListener(event, beginWhenReady),
        );
        hero.heroReadyCleanup = () =>
          events.forEach((event) =>
            activeVideo.removeEventListener(event, beginWhenReady),
          );
        hero.heroAutoTimer = setTimeout(() => {
          if (!hero.heroIsAnimating)
            hero.advanceHeroSequence();
        }, 12000);
        return;
      }
      const delay = Math.max(
        0,
        Math.max(
          minDelay,
          fixedDuration - hero.HERO_TRANSITION_MS - hero.HERO_TRANSITION_BUFFER_MS,
        ) - elapsed,
      );
      hero.heroAutoTimer = setTimeout(() => {
        if (hero.heroIsAnimating) return;
        hero.advanceHeroSequence();
      }, delay);
      return;
    }
    const duration = Number.isFinite(activeVideo.duration)
      ? activeVideo.duration
      : 0;
    if (duration > 0) {
      const leadOut = hero.getHeroVideoLead(activeVideo);
      const delay = Math.max(
        0,
        Math.max(
          minDelay,
          Math.round((duration - startAt) * 1000) - leadOut -
            hero.HERO_TRANSITION_MS - hero.HERO_TRANSITION_BUFFER_MS,
        ) - elapsed,
      );
      hero.heroAutoTimer = setTimeout(() => {
        if (hero.heroIsAnimating) return;
        hero.advanceHeroSequence();
      }, delay);
      return;
    }
    const handleMetadata = () => {
      const currentInternalIndex = hero.getHeroInternalIndex();
      const currentSlide =
        hero.heroTrack.children[currentInternalIndex] || null;
      if (
        currentSlide &&
        currentSlide.contains(activeVideo) &&
        !hero.heroIsAnimating
      ) {
        hero.startHeroAuto();
      }
    };
    activeVideo.addEventListener("loadedmetadata", handleMetadata, {
      once: true,
    });
    hero.heroReadyCleanup = () =>
      activeVideo.removeEventListener("loadedmetadata", handleMetadata);
    hero.heroAutoTimer = setTimeout(() => {
      if (hero.heroIsAnimating) return;
      hero.advanceHeroSequence();
    }, hero.HERO_FALLBACK_MS);
  };

  hero.stopHeroAuto = function stopHeroAuto() {
    if (hero.heroReadyCleanup) {
      hero.heroReadyCleanup();
      hero.heroReadyCleanup = null;
    }
    clearTimeout(hero.heroAutoTimer);
    hero.heroAutoTimer = null;
  };

  hero.restartHeroAuto = function restartHeroAuto() {
    hero.stopHeroAuto();
    if (!hero.heroIsAnimating) hero.startHeroAuto();
  };

  hero.finishHeroSequence = function finishHeroSequence() {
    hero.heroSequenceCompleted = true;
  };

  hero.restartHeroSequence = function restartHeroSequence({ preserveOpening = false } = {}) {
    const first = hero.getHeroDefaultIndex();
    const video = hero.heroTrack?.children[first]?.querySelector('video');
    hero.heroSequenceCompleted = false;
    // The parser's autoplay may already be several frames ahead of our initial render.
    hero.heroLastInternalIndex = preserveOpening && video?.currentTime > 0 && hero.getHeroVideoStart(video) === 0
      ? first : -1;
    hero.heroOutgoingIndex = -1;
    hero.setHeroSlide(first, { animate: false });
  };

  hero.advanceHeroSequence = function advanceHeroSequence() {
    if (!hero.isHeroInView()) return;
    if (hero.heroIndex >= hero.heroSlideCount - 1) {
      hero.finishHeroSequence();
    }
    hero.goHeroNext();
  };

  hero.goHeroNext = function goHeroNext() {
    if (!hero.heroTrack || hero.heroSlideCount < 2 || !hero.isHeroInView())
      return;
    hero.setHeroSlide(hero.heroIndex + 1, { animate: true });
  };

  hero.goHeroPrev = function goHeroPrev() {
    if (!hero.heroTrack || hero.heroSlideCount < 2 || !hero.isHeroInView())
      return;
    hero.setHeroSlide(hero.heroIndex - 1, { animate: true });
  };

  hero.initializeIntro = (initialRoute) => initializeGlassIntro(app, initialRoute);

}

/** Initializers are called explicitly, in dependency order, from main. */
export function prepareIntroOwnership(app) {
  const { hero, layout, navigation, ui } = app;
  hero.proposalHeroReleased = true;
  hero.heroIntroActive = !location.hash || location.hash === "#home";
}

export function initializeCarousel(app) {
  const { hero, layout, navigation, ui } = app;
  hero.heroSlider = document.getElementById("hero-slider");
  hero.heroTrack = document.getElementById("hero-track");
  hero.heroDots = Array.from(document.querySelectorAll(".hero-dot"));
  hero.heroPrev = document.getElementById("hero-prev");
  hero.heroNext = document.getElementById("hero-next");
  hero.heroCopyEl = document.querySelector(".hero-copy");
  hero.heroCopyTitle = hero.heroCopyEl
    ? hero.heroCopyEl.querySelector("h1")
    : null;
  hero.heroCopyText = hero.heroCopyEl
    ? hero.heroCopyEl.querySelector("p")
    : null;
  hero.heroCopyItems = [
    { sk: ['Svätý Jur', 'Rodinný dom. Prepojenie interiéru, terasy a záhrady.'],
      en: ['Svätý Jur', 'Family house. Interior, terrace and garden connected.'] },
    { sk: ['Severné mesto', 'Bytové domy. Architektúra každodenného bývania.'],
      en: ['Severné mesto', 'Residential buildings. Architecture for everyday living.'] },
    { sk: ['Bač', 'Rodinný dom. Priestor medzi domom a záhradou.'],
      en: ['Bač', 'Family house. The space between home and garden.'] },
    { sk: ['plān', 'Rodinný dom Planky. Bývanie v pokojnom lesnom prostredí.'],
      en: ['plān', 'Planky family house. A home in a peaceful forest setting.'] },
  ];
  hero.heroCopyTimer = null;
  hero.HERO_TRANSITION = "transform 1.95s cubic-bezier(.16,1,.3,1)";
  hero.heroOriginalSlides = hero.heroTrack
    ? Array.from(hero.heroTrack.children)
    : [];
  hero.heroSlideCount = hero.heroOriginalSlides.length;
  hero.heroIndex = 0;
  hero.heroTouchStartX = 0;
  hero.heroTouchDeltaX = 0;
  hero.heroAutoTimer = null;
  hero.heroReadyCleanup = null;
  hero.heroIsAnimating = false;
  hero.heroLastInternalIndex = -1;
  hero.heroTransitionTimer = null;
  hero.heroPendingTransitionId = 0;
  hero.heroSequenceCompleted = false;
  hero.heroOutgoingIndex = -1;
  hero.heroPreloadedVideos = new Set();
  hero.heroActivatedVideos = new WeakSet();
  hero.heroNeighborWarmQueued = new WeakSet();
  hero.heroPreloadedImages = new Set();
  hero.HERO_FALLBACK_MS = 5200;
  hero.HERO_TRANSITION_MS = 1950;
  hero.HERO_TRANSITION_BUFFER_MS = 90;
  hero.HERO_COPY_TRANSITION_MS = Math.round(hero.HERO_TRANSITION_MS * 0.44);
  hero.HERO_COPY_SWAP_DELAY_MS = Math.round(hero.HERO_TRANSITION_MS * 0.5);
  if (hero.heroCopyEl) {
    hero.heroCopyEl.style.setProperty(
      "--hero-copy-transition-ms",
      `${hero.HERO_COPY_TRANSITION_MS}ms`,
    );
  }
  if (hero.heroSlideCount) {
    hero.renderHeroSlideCopies();
    if (hero.heroPrev)
      hero.heroPrev.addEventListener("click", () => {
        hero.goHeroPrev();
      });
    if (hero.heroNext)
      hero.heroNext.addEventListener("click", () => {
        hero.goHeroNext();
      });
    hero.heroDots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const target = Number(dot.dataset.slide);
        if (!Number.isFinite(target)) return;
        hero.setHeroSlide(target, { animate: true });
      });
    });
    if (hero.heroSlider) {
      if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(hero.syncHeroViewport, { threshold: [0, 0.001] });
        observer.observe(hero.heroSlider);
      } else {
        window.addEventListener("scroll", hero.syncHeroViewport, { passive: true });
        window.addEventListener("resize", hero.syncHeroViewport);
      }
      hero.heroSlider.addEventListener(
        "touchstart",
        (e) => {
          hero.heroTouchStartX = e.changedTouches[0].clientX;
          hero.heroTouchDeltaX = 0;
          hero.stopHeroAuto();
        },
        { passive: true },
      );
      hero.heroSlider.addEventListener(
        "touchmove",
        (e) => {
          hero.heroTouchDeltaX =
            e.changedTouches[0].clientX - hero.heroTouchStartX;
        },
        { passive: true },
      );
      hero.heroSlider.addEventListener(
        "touchend",
        () => {
          const threshold = 45;
          if (hero.heroTouchDeltaX > threshold) hero.goHeroPrev();
          else if (hero.heroTouchDeltaX < -threshold) hero.goHeroNext();
          hero.restartHeroAuto();
        },
        { passive: true },
      );
    }
  }
  document.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    if (ui.shouldIgnoreArrowNavigation(event)) return;
    const openLightbox = document.getElementById("project-lightbox");
    if (openLightbox && openLightbox.classList.contains("is-open")) return;
    const activeProjectShowcase = document.querySelector(
      ".project-detail-view.active-view .project-showcase",
    );
    if (activeProjectShowcase) {
      const targetBtn =
        event.key === "ArrowLeft"
          ? activeProjectShowcase.querySelector(".project-showcase-btn.prev")
          : activeProjectShowcase.querySelector(".project-showcase-btn.next");
      if (targetBtn) {
        event.preventDefault();
        targetBtn.click();
      }
      return;
    }
    if (hero.isHeroInView() && !event.target.closest?.(".home-preview-canvas, footer")) {
      const targetBtn =
        event.key === "ArrowLeft" ? hero.heroPrev : hero.heroNext;
      if (targetBtn) {
        event.preventDefault();
        targetBtn.click();
      }
    }
  });
}

export function bindHeroInteractions(app) {
  const { hero, layout, navigation, ui } = app;
  hero.userInteracted = false;
  hero.stopAutoScroll = () => {
    if (!hero.isHeroHomeActive()) return;
    hero.userInteracted = true;
    hero.heroSequenceCompleted = true;
  };
  window.addEventListener("wheel", hero.stopAutoScroll, { passive: true });
  window.addEventListener("touchmove", hero.stopAutoScroll, { passive: true });
  window.addEventListener(
    "scroll",
    () => {
      if (!hero.isHeroHomeActive()) return;
      if ((layout.getScrollTop?.() ?? window.scrollY) > 8) hero.stopAutoScroll();
    },
    { passive: true },
  );
  window.addEventListener("keydown", (event) => {
    if (!hero.isHeroHomeActive()) return;
    if (!["ArrowDown", "PageDown", "Space", "End", "Home"].includes(event.code))
      return;
    hero.stopAutoScroll();
  });
}
